import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_API_URL || '/api'}/support-logos`;

export const getSupportLogos = createAsyncThunk(
  'supportLogos/getSupportLogos',
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(API_URL);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const createSupportLogo = createAsyncThunk(
  'supportLogos/createSupportLogo',
  async (logoData, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user?.token;
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      };
      const response = await axios.post(API_URL, logoData, config);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const updateSupportLogo = createAsyncThunk(
  'supportLogos/updateSupportLogo',
  async ({ id, logoData }, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user?.token;
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      };
      const response = await axios.put(`${API_URL}/${id}`, logoData, config);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const deleteSupportLogo = createAsyncThunk(
  'supportLogos/deleteSupportLogo',
  async (id, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user?.token;
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      await axios.delete(`${API_URL}/${id}`, config);
      return id;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return thunkAPI.rejectWithValue(message);
    }
  }
);

const initialState = {
  logos: [],
  isLoading: false,
  isSuccess: false,
  isError: false,
  message: ''
};

const supportLogosSlice = createSlice({
  name: 'supportLogos',
  initialState,
  reducers: {
    reset: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
      state.message = '';
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSupportLogos.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getSupportLogos.fulfilled, (state, action) => {
        state.isLoading = false;
        state.logos = action.payload;
      })
      .addCase(getSupportLogos.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(createSupportLogo.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createSupportLogo.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.logos.push(action.payload);
      })
      .addCase(createSupportLogo.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(updateSupportLogo.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateSupportLogo.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        const index = state.logos.findIndex(logo => logo._id === action.payload._id);
        if (index !== -1) {
          state.logos[index] = action.payload;
        }
      })
      .addCase(updateSupportLogo.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(deleteSupportLogo.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteSupportLogo.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.logos = state.logos.filter(logo => logo._id !== action.payload);
      })
      .addCase(deleteSupportLogo.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      });
  }
});

export const { reset } = supportLogosSlice.actions;
export default supportLogosSlice.reducer;
